import React, { useMemo, useState } from 'react';
import { Form, Button, Input, Spin, Popconfirm } from 'antd';
import { ReactSVG } from 'react-svg';
import { useDispatch } from 'react-redux';
import { doCreateSetting, doDeleteSetting, doUpdateSetting } from '../../../../modules/settings/store/thunk';
import { SendTestMail } from './SendTestMail';
import { doSendTestMail } from '../../../../modules/sendgrid/store/thunk';

export const SetupSendgrid = ({ isFetching, done, existing }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const [isSaving, setIsSaving] = useState(false);

  const handleCancel = () => {
    form.resetFields();
    done();
  };

  // these keywords correspond to the allowed keywords in the backend
  const sendGridSettingTypes = [
    {
      label: 'API Token',
      keyword: 'API_KEY',
    },
    {
      label: 'Template ID',
      keyword: 'TEMPLATE_ID',
    },
  ];

  // construct settings array with potentially existing values
  const [settings] = useState(
    sendGridSettingTypes.map((s) => ({
      label: s.label,
      keyword: s.keyword,
      value: existing.find((e) => e.keyword === s.keyword)?.value || null,
    })),
  );

  const submit = async (values) => {
    setIsSaving(true);
    // construct param objects from input
    const updates = sendGridSettingTypes.map((s) => ({
      id: existing.find((e) => s.keyword === e.keyword)?.id || null,
      keyword: s.keyword,
      value: values[s.keyword] || null,
      group: 'SENDGRID',
    }));

    // send updates or creates to backend depending on their existance
    await Promise.all(
      updates.map(async (u) => {
        if (u.id && u.value) {
          // if it existed and has a value => update it
          await dispatch(doUpdateSetting(u));
        } else if (u.id && !u.value) {
          // if it existed and does not have a value => delete it
          await dispatch(doDeleteSetting(u));
        } else if (u.value) {
          // if it didn't exist => create it
          await dispatch(doCreateSetting(u));
        }
      }),
    );
    done({ refetch: true });
  };

  const turnOff = async () => {
    // delete all existing sendgrid settings
    await Promise.all(
      existing.map(async (e) => {
        if (e.id) {
          await dispatch(doDeleteSetting({ id: e.id }));
        }
      }),
    );
    done({ refetch: true });
  };

  const spinning = useMemo(() => isFetching || isSaving, [isFetching, isSaving]);

  const sendTest = () => {
    const raw = form.getFieldsValue();
    const body = {
      token: raw.API_KEY,
      templateId: raw.TEMPLATE_ID,
    };
    dispatch(doSendTestMail(body));
  };

  return (
    <Spin spinning={spinning}>
      <div className="RoutePlanForm">
        <Form form={form} onFinish={submit} layout="vertical" requiredMark={false} colon={false}>
          {settings.map((s) => (
            <Form.Item label={s.label} value={s.value} initialValue={s.value} name={s.keyword} key={s.keyword}>
              <Input placeholder="" size="large" />
            </Form.Item>
          ))}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <SendTestMail send={sendTest} style={{ marginBottom: '4px' }} />
          </div>

          <div className="FormFooter">
            {existing.length > 0 && (
              <Popconfirm
                title="Are you sure to turn off Sendgrid integration?"
                onConfirm={turnOff}
                okText="Yes"
                cancelText="No"
              >
                <Button key="delete" size="small" danger>
                  Turn off sendgrid
                </Button>
              </Popconfirm>
            )}

            <Button key="cancel" size="small" onClick={handleCancel}>
              Cancel
            </Button>
            <Button key="confirm" type="primary" size="small" htmlType="submit">
              {existing ? 'Update' : 'Add'} integration
              <ReactSVG className="icon" src="/icons/done.svg" />
            </Button>
          </div>
        </Form>
      </div>
    </Spin>
  );
};
